import { AsyncPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  Signal,
  WritableSignal,
  computed,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { MatIcon } from '@angular/material/icon';
import { LetDirective } from '@ngrx/component';
import { Observable } from 'rxjs';

import { Pagination, PaginationMetadata } from '@core/shared/domain';
import {
  HeaderElementDirective,
  RestoreScrollingDirective,
  RestoreScrollingTriggerDirective,
  filterUndefined,
} from '@core/shared/util';
import {
  DropdownComponent,
  ListHeaderComponent,
  PaginatorComponent,
  RootModuleNavComponent,
  SelectOption,
  SpinnerComponent,
} from '@core/ui';
import {
  IndustryCatalog,
  IndustryCatalogFilter,
  IndustryCatalogInRelease,
  IndustryCatalogSorting,
  PlattformPackungseinheit,
  PlattformPackungseinheitMapping,
  UpdateIndustryCatalog,
  injectPlatformSuppliersQuery,
} from '@mp/content-manager/industriekataloge/data-access';
import { IndustryCatalogCardComponent } from '@mp/content-manager/industriekataloge/ui';
import { QUERY_PARAMS_FILTER_TOKEN, QueryParamsStoreFilterFacade } from '@mp/shared/data-access';
import { PlatformPackagingUnitsPermissions } from '@mp/system/platform-packaging-units/feature';
import { LoginContextService } from '@mpk/shared/data-access';
import { PlatformSupplierBasic } from '@mpsm/shared/domain';

import { FreigabeQueueComponent } from '../freigabe-queue/freigabe-queue.component';
import { IndustriekatalogeQueryParamsFilterConverter } from '../industriekataloge-query-params-filter-converter.facade';
import { IndustriekatalogeFacade } from '../industriekataloge.facade';
import { PackungseinheitenDialogComponent } from '../packungseinheiten-dialog/packungseinheiten-dialog.component';
import { IndustryCatalogsPermissions } from '../permissions';

import { IndustriekatalogeFilterAreaComponent } from './industriekataloge-filter-area/industriekataloge-filter-area.component';

@Component({
  selector: 'mp-industriekataloge-list',
  templateUrl: './industriekataloge-list.component.html',
  styleUrl: './industriekataloge-list.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AsyncPipe,
    LetDirective,

    MatIcon,

    ListHeaderComponent,
    PackungseinheitenDialogComponent,
    DropdownComponent,
    HeaderElementDirective,
    RootModuleNavComponent,
    IndustriekatalogeFilterAreaComponent,
    RestoreScrollingDirective,
    FreigabeQueueComponent,
    RestoreScrollingTriggerDirective,
    PaginatorComponent,
    SpinnerComponent,
    IndustryCatalogCardComponent,
  ],
  providers: [
    {
      provide: QUERY_PARAMS_FILTER_TOKEN,
      useClass: IndustriekatalogeQueryParamsFilterConverter,
    },
    QueryParamsStoreFilterFacade,
  ],
})
export class IndustriekatalogeListComponent implements OnInit {
  readonly filter$: Observable<IndustryCatalogFilter> = this.filterFacade.filter$;
  readonly kataloge: Signal<IndustryCatalog[]> = toSignal<IndustryCatalog[]>(this.facade.kataloge$, {
    requireSync: true,
  });
  readonly dataSourcePackungseinheiten$: Observable<PlattformPackungseinheitMapping[]> =
    this.facade.dataSourcePackungseinheitenMappings$;
  readonly dataSourcePackagingUnitPagination$: Observable<PaginationMetadata> =
    this.facade.dataSourcePackagingUnitPagination$.pipe(filterUndefined());

  readonly plattformPackungseinheiten$: Observable<PlattformPackungseinheit[]> =
    this.facade.plattformPackungseinheiten$;
  readonly datasources$: Observable<string[]> = this.facade.platformDataSources$;
  readonly industryCatalogsDatasource$: Observable<string[]> = this.facade.industryCatalogsDatasource$;
  readonly pagination$: Observable<PaginationMetadata> = this.facade.pagination$.pipe(filterUndefined());
  readonly isLoading$: Observable<boolean> = this.facade.isLoading$;

  readonly searchTerm: WritableSignal<string> = signal('');
  readonly platformSupplierQuery = injectPlatformSuppliersQuery(this.searchTerm);

  readonly platformSuppliers: Signal<PlatformSupplierBasic[]> = computed(
    () => this.platformSupplierQuery.data()?.data ?? [],
  );

  readonly isLoadingPlatformSuppliers: Signal<boolean> = this.platformSupplierQuery.isLoading;

  protected readonly hasManageIndustryCatalogsPermission = this.loginContextService.permission(
    IndustryCatalogsPermissions.ManageIndustryCatalogs,
  );
  // TODO: Permissions of another module must not be checked here. PlatformPackagingUnits in fact is ContentManagement and should be moved here.
  readonly hasManagePlatformPackagingUnitsPermission = this.loginContextService.permission(
    PlatformPackagingUnitsPermissions.ManagePlatformPackagingUnits,
  );

  readonly sortCriteriaOptions: SelectOption<IndustryCatalogSorting>[] = [
    { label: 'Name (aufsteigend)', value: 'name' },
    { label: 'Name (absteigend)', value: '-name' },
    { label: 'Letzter Import (aufsteigend)', value: 'lastStaging' },
    { label: 'Letzter Import (absteigend)', value: '-lastStaging' },
  ];

  readonly queueFetcher: () => Observable<IndustryCatalogInRelease[]> = (): Observable<IndustryCatalogInRelease[]> =>
    this.facade.getAllInFreigabe();

  constructor(
    private facade: IndustriekatalogeFacade,
    private filterFacade: QueryParamsStoreFilterFacade<IndustryCatalogFilter>,
    private readonly destroyRef: DestroyRef,
    private readonly loginContextService: LoginContextService,
  ) {}

  ngOnInit(): void {
    this.filterFacade.filter$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((filter: IndustryCatalogFilter): void => {
        this.facade.getAll(filter);
      });

    this.getIndustryCatalogsDatasources();
  }

  getIndustryCatalogsDatasources(): void {
    this.facade.getIndustryCatalogsDatasources();
  }

  getAllDatasources(): void {
    this.facade.getAllDatasources();
  }

  handleFilterChanges(filters: Partial<IndustryCatalogFilter>): void {
    this.filterFacade.updateFilterAndResetPage(filters);
  }

  handleSearchTermChanges(searchTerm: string): void {
    this.filterFacade.updateFilterAndResetPage({ searchTerm });
  }

  handleSupplierSearchTermChanges(searchTerm: string): void {
    this.searchTerm.set(searchTerm);
  }

  handleSortCriteriaChanges(sortCriteria: IndustryCatalogSorting | undefined): void {
    // Sorting does not change the query result, so we don't need to reset the page:
    this.filterFacade.updateFilter({ sortCriteria });
  }

  getNextPage(pageChangePagination: Pagination): void {
    this.filterFacade.updateFilter(pageChangePagination);
  }

  updateKatalog(catalogId: string, katalogFreigeben: UpdateIndustryCatalog): void {
    this.facade.updateIndustriekatalog(catalogId, katalogFreigeben, this.filterFacade.filter);
  }

  navigateToIndustriekatalogDetail(id: string): void {
    this.facade.navigateToIndustriekatalogDetail(id);
  }
}
